import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Badge as MBadge, Box, makeStyles } from '@material-ui/core';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import { useQuery } from '@tanstack/react-query';

import { adminListOffersCountApi } from '../../api/offers/adminListOffersCountApi';
import { supportCountReservationsForReviewApi } from '../../api/reservations/supportCountReservationsForReviewApi';
import { adminGetUnreadUserComplaintCountApi } from '../../api/supports/adminGetUnreadUserComplaintCountApi';
import { adminListAgenciesCountApi } from '../../api/venues/adminListAgenciesCountApi';
import { OfferStatus, ReservsationsSupportListType, VenueStatus } from '../../graphql/API';
import { useChatSupportTicketCounts } from '../../hooks/reactQuery/chat/useChatSupportTicketCounts';
import { useAdminRole } from '../../hooks/useAdminRole';
import { icons } from '../../themes/icons';
import { COMPLAINT_TYPES } from '../Tables/SupportsTable/types';

const UserResearchIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    {/* Body / shoulders */}
    <path d="M12 14c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    {/* Head: circle outline only — filled nodes inside create the "brain" */}
    <circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
    {/* Neural-network nodes */}
    <circle cx="12" cy="8" r="1.2" />
    <circle cx="12" cy="5.2" r="0.85" />
    <circle cx="9.6" cy="9.8" r="0.85" />
    <circle cx="14.4" cy="9.8" r="0.85" />
    {/* Connections between centre and satellite nodes */}
    <line x1="12" y1="6.8" x2="12" y2="6.05" stroke="currentColor" strokeWidth="0.8" />
    <line x1="11.1" y1="8.7" x2="10.2" y2="9.3" stroke="currentColor" strokeWidth="0.8" />
    <line x1="12.9" y1="8.7" x2="13.8" y2="9.3" stroke="currentColor" strokeWidth="0.8" />
  </svg>
);

const Badge = styled(MBadge)({
  span: {
    backgroundColor: 'rgba(241, 13, 0, 1)',
    transform: 'scale(1) translate(-40%, -40%)',
  },
});

const OfferBadge = styled(MBadge)({
  span: {
    backgroundColor: 'rgba(241, 13, 0, 1)',
    transform: 'scale(1) translate(5%, -40%)',
  },
});

const AgenciesBadge = styled(MBadge)({
  span: {
    backgroundColor: 'rgba(241, 13, 0, 1)',
    transform: 'scale(1) translate(-30%, -50%)',
  },
});

interface MenuItemsWithHeading {
  heading: string;
  tabs: MenuItem[];
}
interface MenuItem {
  label: string;
  route: string;
  icon: React.FC;
  exact?: boolean;
  withDivider?: boolean;
}

const menuVenue = (venueId?: string) => {
  const menuItems: MenuItemsWithHeading[] = [
    {
      heading: 'Main',
      tabs: [
        {
          label: 'Businesses',
          route: `/businesses/${venueId}/profile`,
          icon: icons.StorefrontIcon,
        },
        { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon },
        { label: 'Reservations', route: '/reservations', icon: icons.EventAvailableIcon },
      ],
    },
  ];

  return menuItems;
};

const menuItemsSuperAdmin = ({
  supportCount,
  offerPendingCount,
  pendingAgenciesCount,
  unreadChatCount,
  fastCheckPendingCount,
}: {
  supportCount?: number;
  offerPendingCount?: number;
  pendingAgenciesCount?: number;
  unreadChatCount?: number;
  fastCheckPendingCount?: number;
}): MenuItemsWithHeading[] => [
  {
    heading: 'Main',
    tabs: [
      { label: 'Dashboard', route: '/', icon: icons.DashboardIcon, exact: true },
      {
        label: 'Model Applications',
        route: '/user-applications',
        icon: () => <icons.ModelIcon />,
        exact: true,
      },
      {
        label: 'Business Applications',
        route: '/business-applications',
        icon: () => <icons.VenueIcon />,
        exact: true,
      },
      {
        label: 'Offer Applications',
        route: '/offer-applications',
        icon: () => (
          <OfferBadge color="primary" badgeContent={offerPendingCount}>
            <icons.OfferIcon />
          </OfferBadge>
        ),
        exact: true,
      },
      { label: 'Referrals', route: '/referrals', icon: icons.ArrowUpwardIcon },
    ],
  },
  {
    heading: 'Support',
    tabs: [
      {
        label: 'Chat',
        route: '/chats',
        icon: () => (
          <Badge color="primary" badgeContent={unreadChatCount}>
            <icons.ChatIcon />
          </Badge>
        ),
      },
      {
        label: 'Profile updates',
        route: '/profile-updates',
        icon: () => (
          <Badge color="primary" badgeContent={supportCount}>
            <icons.SupportIcon />
          </Badge>
        ),
      },
      {
        label: 'Fast Check',
        route: '/fast-check-reservations',
        icon: () => (
          <Badge color="primary" badgeContent={fastCheckPendingCount}>
            <icons.FastForward />
          </Badge>
        ),
      },
      { label: 'Support AI', route: '/support-ai', icon: UserResearchIcon },
    ],
  },
  {
    heading: 'Database',
    tabs: [
      { label: 'Models', route: '/users', icon: icons.PeopleIcon },
      { label: 'Leaderboard', route: '/leaderboard', icon: icons.EqualizerIcon },
      { label: 'Businesses', route: '/businesses', icon: icons.StorefrontIcon },
      { label: 'Subscriptions', route: '/subscriptions', icon: icons.CardGiftcardIcon },
      { label: 'Markets', route: '/markets', icon: icons.LocationCityIcon },
      { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon },
      { label: 'Events', route: '/event', icon: icons.CalendarTodayIcon },
      { label: 'Content', route: '/content', icon: icons.CollectionsIcon },
      {
        label: 'Agencies',
        route: '/agencies',
        icon: () => (
          <AgenciesBadge color="primary" badgeContent={pendingAgenciesCount}>
            <icons.SheildIcon />
          </AgenciesBadge>
        ),
      },
    ],
  },
  {
    heading: 'Core Action',
    tabs: [
      { label: 'Reservations', route: '/reservations', icon: icons.EventAvailableIcon },
      { label: 'Collabs', route: '/collabs', icon: icons.EmailIcon },
      { label: 'Scouting', route: '/scouting', icon: icons.ScoutingIcon },
      { label: 'No Shows', route: '/no-shows', icon: icons.CancelIcon },
      { label: 'Penalties', route: '/penalties', icon: icons.WarningIcon },
      { label: 'Suspensions', route: '/suspensions', icon: icons.BlockIcon },
    ],
  },
  {
    heading: 'Admin',
    tabs: [
      { label: 'Campaigns', route: '/campaigns', icon: icons.NotificationsActiveIcon },
      { label: 'Staff', route: '/staff', icon: icons.FaceIcon },
      { label: 'Terms & Conditions', route: '/terms-and-conditions', icon: icons.AssignmentIcon },
      { label: 'Logs', route: '/logs', icon: icons.ViewStreamIcon },
      { label: 'Instagram Accounts', route: '/instagram-accounts', icon: icons.PeopleIcon },
      { label: 'Search Businesses', route: '/search-businesses', icon: icons.SearchIconV2 },
    ],
  },
];

const menuItemsAdmin = ({
  supportCount,
  offerPendingCount,
  pendingAgenciesCount,
  unreadChatCount,
  fastCheckPendingCount,
}: {
  supportCount?: number;
  offerPendingCount?: number;
  pendingAgenciesCount?: number;
  unreadChatCount?: number;
  fastCheckPendingCount?: number;
}): MenuItemsWithHeading[] => [
  {
    heading: 'Main',
    tabs: [
      { label: 'Dashboard', route: '/', icon: icons.DashboardIcon, exact: true },
      {
        label: 'Model Applications',
        route: '/user-applications',
        icon: () => <icons.ModelIcon />,
        exact: true,
      },
      {
        label: 'Business Applications',
        route: '/business-applications',
        icon: () => <icons.VenueIcon />,
        exact: true,
      },
      {
        label: 'Offer Applications',
        route: '/offer-applications',
        icon: () => (
          <OfferBadge color="primary" badgeContent={offerPendingCount}>
            <icons.OfferIcon />
          </OfferBadge>
        ),
        exact: true,
      },
      { label: 'Referrals', route: '/referrals', icon: icons.ArrowUpwardIcon },
    ],
  },
  {
    heading: 'Support',
    tabs: [
      {
        label: 'Chat',
        route: '/chats',
        icon: () => (
          <Badge color="primary" badgeContent={unreadChatCount}>
            <icons.ChatIcon />
          </Badge>
        ),
      },
      {
        label: 'Profile updates',
        route: '/profile-updates',
        icon: () => (
          <Badge color="primary" badgeContent={supportCount}>
            <icons.SupportIcon />
          </Badge>
        ),
      },
      {
        label: 'Fast Check',
        route: '/fast-check-reservations',
        icon: () => (
          <Badge color="primary" badgeContent={fastCheckPendingCount}>
            <icons.FastForward />
          </Badge>
        ),
      },
      { label: 'Support AI', route: '/support-ai', icon: UserResearchIcon },
    ],
  },
  {
    heading: 'Database',
    tabs: [
      { label: 'Models', route: '/users', icon: icons.PeopleIcon },
      { label: 'Leaderboard', route: '/leaderboard', icon: icons.EqualizerIcon },
      { label: 'Businesses', route: '/businesses', icon: icons.StorefrontIcon },
      { label: 'Subscriptions', route: '/subscriptions', icon: icons.CardGiftcardIcon },
      { label: 'Markets', route: '/markets', icon: icons.LocationCityIcon },
      { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon },
      { label: 'Events', route: '/event', icon: icons.CalendarTodayIcon },
      { label: 'Content', route: '/content', icon: icons.CollectionsIcon },
      {
        label: 'Agencies',
        route: '/agencies',
        icon: () => (
          <AgenciesBadge color="primary" badgeContent={pendingAgenciesCount}>
            <icons.SheildIcon />
          </AgenciesBadge>
        ),
      },
    ],
  },
  {
    heading: 'Core Action',
    tabs: [
      { label: 'Reservations', route: '/reservations', icon: icons.EventAvailableIcon },
      { label: 'Collabs', route: '/collabs', icon: icons.EmailIcon },
      { label: 'Scouting', route: '/scouting', icon: icons.ScoutingIcon },
      { label: 'No Shows', route: '/no-shows', icon: icons.CancelIcon },
      { label: 'Penalties', route: '/penalties', icon: icons.WarningIcon },
      { label: 'Suspensions', route: '/suspensions', icon: icons.BlockIcon },
    ],
  },
  {
    heading: 'Admin',
    tabs: [
      { label: 'Campaigns', route: '/campaigns', icon: icons.NotificationsActiveIcon },
      { label: 'Instagram Accounts', route: '/instagram-accounts', icon: icons.PeopleIcon },
      { label: 'Search Businesses', route: '/search-businesses', icon: icons.SearchIconV2 },
    ],
  },
];

const menuItemsSupport = (
  supportCount?: number,
  unreadChatCount?: number,
  totalFastCheckPending?: number,
): MenuItemsWithHeading[] => [
  {
    heading: 'Main',
    tabs: [
      { label: 'Dashboard', route: '/', icon: icons.DashboardIcon, exact: true },
      { label: 'Events', route: '/event', icon: icons.CalendarTodayIcon },
    ],
  },
  {
    heading: 'Support',
    tabs: [
      {
        label: 'Chat',
        route: '/chats',
        icon: () => (
          <Badge color="primary" badgeContent={unreadChatCount}>
            <icons.ChatIcon />
          </Badge>
        ),
      },
      {
        label: 'Profile updates',
        route: '/profile-updates',
        icon: () => (
          <Badge color="primary" badgeContent={supportCount}>
            <icons.SupportIcon />
          </Badge>
        ),
      },
      {
        label: 'Fast Check',
        route: '/fast-check-reservations',
        icon: () => (
          <Badge color="primary" badgeContent={totalFastCheckPending}>
            <icons.FastForward />
          </Badge>
        ),
      },
    ],
  },
];

const menuItemsFastCheck = (totalFastCheckPending?: number): MenuItemsWithHeading[] => [
  {
    heading: 'Main',
    tabs: [
      { label: 'Dashboard', route: '/', icon: icons.DashboardIcon, exact: true },
      { label: 'Events', route: '/event', icon: icons.CalendarTodayIcon },
      {
        label: 'Fast Check',
        route: '/fast-check-reservations',
        icon: () => (
          <Badge color="primary" badgeContent={totalFastCheckPending}>
            <icons.FastForward />
          </Badge>
        ),
      },
    ],
  },
];

const menuItemsSocial: MenuItemsWithHeading[] = [
  {
    heading: 'Main',
    tabs: [
      { label: 'Content', route: '/content', icon: icons.CollectionsIcon },
      { label: 'Events', route: '/event', icon: icons.CalendarTodayIcon },
    ],
  },
];

const menuItemsCityManager = ({
  unreadChatCount,
}: {
  unreadChatCount?: number;
}): MenuItemsWithHeading[] => [
  {
    heading: 'Main',
    tabs: [
      { label: 'Dashboard', route: '/', icon: icons.DashboardIcon, exact: true },
      { label: 'Referrals', route: '/referrals', icon: icons.ArrowUpwardIcon },
    ],
  },
  {
    heading: 'Support',
    tabs: [
      {
        label: 'Chat',
        route: '/chats',
        icon: () => (
          <Badge color="primary" badgeContent={unreadChatCount}>
            <icons.ChatIcon />
          </Badge>
        ),
      },
    ],
  },
  {
    heading: 'Database',
    tabs: [
      {
        label: 'Business Applications',
        route: '/business-applications',
        icon: () => <icons.StorefrontIcon />,
        exact: true,
      },
      {
        label: 'Offer Applications',
        route: '/offer-applications',
        icon: () => <icons.OfferIcon />,
        exact: true,
      },
      { label: 'Markets', route: '/markets', icon: icons.LocationCityIcon },
      { label: 'Businesses', route: '/businesses', icon: icons.StorefrontIcon },
      { label: 'Offers', route: '/offers', icon: icons.LocalOfferIcon },
      { label: 'Events', route: '/event', icon: icons.CalendarTodayIcon },
      { label: 'Campaigns', route: '/campaigns', icon: icons.NotificationsActiveIcon },
    ],
  },
];

const StyledTab = styled(({ ...rest }) => (
  <Tab
    title={rest.label}
    classes={{
      wrapper: 'wrapper',
      labelIcon: 'label-icon',
    }}
    {...rest}
  />
))`
  .wrapper {
    align-items: flex-start;
    display: inline-flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    flex-shrink: 0;
    min-width: 100%;
    white-space: nowrap;
    font-size: 15px;
    text-transform: capitalize;
    font-weight: 400;
    font-family: Poppins;

    & svg {
      margin-right: 1.3rem;
    }
  }

  &.label-icon {
    min-height: 2rem;

    div {
      position: relative;

      span {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        left: 0.5rem;
        bottom: 0.1rem;
        width: 1.45rem;
        height: 1rem;
        background: white;
        border-radius: 0.3rem;
        overflow: hidden;
        border: 1px solid currentColor;

        :before {
          position: absolute;
          background: currentColor;
          content: '';
          width: 100%;
          height: 100%;
        }

        svg {
          height: 1.6rem;
          width: 1.8rem;
          margin-left: 0.05rem;
          margin-right: 0;
          fill: white;
          z-index: 2;
        }
      }
    }
  }
`;

const renderMaterialTab = (item: MenuItem, sidebarLocker: boolean) => {
  const ItemIcon = item.icon;

  return (
    <StyledTab
      key={`sidebar_with_${item.route}`}
      icon={<ItemIcon />}
      label={sidebarLocker ? item.label : ''}
      component={Link}
      to={item.route}
      value={item.route}
    />
  );
};

const StyledTabs = styled(({ children, ...rest }) => (
  <Tabs classes={{ scroller: 'scroller', indicator: 'indicator' }} {...rest}>
    {children}
  </Tabs>
))`
  .indicator {
    left: 0px;
    width: 3px;
  }
  .scroller {
    overflow-y: visible !important;
  }
`;

export interface Props {
  sidebarLocked: boolean;
  toggleSidebarLocked(): void;
}

const useStyles = makeStyles(() => ({
  container: {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
}));

export const Sidebar: React.FC<Props> = ({ sidebarLocked, toggleSidebarLocked }) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const [selected, setSelected] = useState<string>('/');
  const {
    isSupport,
    isCityManager,
    isVenue,
    venueId,
    isSocial,
    isAdmin,
    isFastCheck,
    isSuperAdmin,
  } = useAdminRole();

  const { data: supportUnreadCount } = useQuery({
    queryKey: ['list-support', 'support-count', 'unread-count'],
    queryFn: () => adminGetUnreadUserComplaintCountApi({ types: COMPLAINT_TYPES }),
    enabled: isAdmin,
  });

  const { userSupportCount, venueSupportCount } = useChatSupportTicketCounts({
    type: '',
  });

  const { data: offersCount } = useQuery({
    queryKey: ['offers-pending-count'],
    queryFn: () =>
      adminListOffersCountApi({
        status: OfferStatus.pending,
        search: '',
        sortBy: 'createdAt',
        sortDirection: 'desc',
        marketID: '',
        category: null,
      }),
    enabled: isAdmin,
  });

  const { data: pendingAgenciesCount } = useQuery({
    queryKey: ['pending-agencies-count'],
    queryFn: () =>
      adminListAgenciesCountApi({
        status: VenueStatus.pending,
      }),
    enabled: isAdmin,
  });

  const { data: fastChecksCount } = useQuery({
    queryKey: ['fast-check-count'],
    queryFn: () =>
      supportCountReservationsForReviewApi({
        type: ReservsationsSupportListType.pending,
      }),
    enabled: !isSocial,
  });

  const totalFastCheckPending = React.useMemo(
    () =>
      fastChecksCount?.reduce((total, item) => {
        return total + (item.count ?? 0);
      }, 0),
    [fastChecksCount],
  );

  const menuVenueMemo = React.useMemo(() => {
    return menuVenue(venueId);
  }, [venueId]);

  const menuItems = isSupport
    ? menuItemsSupport(
        supportUnreadCount,
        (userSupportCount?.open || 0) + (venueSupportCount?.open || 0),
        totalFastCheckPending,
      )
    : isSocial
    ? menuItemsSocial
    : isCityManager
    ? menuItemsCityManager({
        unreadChatCount: (userSupportCount?.open || 0) + (venueSupportCount?.open || 0),
      })
    : isFastCheck
    ? menuItemsFastCheck(totalFastCheckPending)
    : isVenue
    ? menuVenueMemo
    : isSuperAdmin
    ? menuItemsSuperAdmin({
        supportCount: supportUnreadCount,
        offerPendingCount: offersCount ? offersCount['totalPending'] : undefined,
        pendingAgenciesCount: pendingAgenciesCount,
        unreadChatCount: (userSupportCount?.open || 0) + (venueSupportCount?.open || 0),
        fastCheckPendingCount: totalFastCheckPending,
      })
    : menuItemsAdmin({
        supportCount: supportUnreadCount,
        offerPendingCount: offersCount ? offersCount['totalPending'] : undefined,
        pendingAgenciesCount: pendingAgenciesCount,
        unreadChatCount: (userSupportCount?.open || 0) + (venueSupportCount?.open || 0),
        fastCheckPendingCount: totalFastCheckPending,
      });

  useEffect(() => {
    setSelected(pathname);
  }, [pathname]);

  const handleChange = useCallback((e, newValue) => {
    setSelected(newValue);
  }, []);

  return (
    <SideBarContainer sidebarLocked={sidebarLocked}>
      <Box height={'100%'} overflow={'auto'} className={classes.container}>
        {menuItems.map((menu, index) => (
          <Box
            display={'flex'}
            flexDirection={'column'}
            mt={index === 0 ? '16px' : 0}
            mb={sidebarLocked ? '40px' : ''}
          >
            {sidebarLocked && <TabsHeading>{menu.heading}</TabsHeading>}
            <StyledTabs
              orientation="vertical"
              value={selected}
              indicatorColor="primary"
              textColor="primary"
              onChange={handleChange}
            >
              {menu.tabs.map((item) => renderMaterialTab(item, sidebarLocked))}
            </StyledTabs>
          </Box>
        ))}
      </Box>
      <Lock onClick={toggleSidebarLocked} sidebarLocked={sidebarLocked}>
        <icons.DoubleArrowIcon />
      </Lock>
    </SideBarContainer>
  );
};

const SideBarContainer = styled.div<{ sidebarLocked: boolean }>`
  ${({ sidebarLocked, theme }) => `
  background-color: ${theme.colors.sidebarBackground};
  bottom: 0;
  display: flex;
  flex-flow: column nowrap;
  left: 0;
  position: fixed;
  top: 0;
  z-index: ${theme.zIndex.sidebar};
  max-width: ${sidebarLocked ? theme.sizes.sidebarWidth : theme.sizes.shrinkedSidebarWidth};
  min-width: ${sidebarLocked ? theme.sizes.sidebarWidth : theme.sizes.shrinkedSidebarWidth};
  overflow-x: hidden;
  overflow-y: auto !important;
  padding-bottom: 50px;
  transition: all 100ms ease-in-out;
  height: 100vh;
  padding-top: ${theme.sizes.headerHeight};
  border-right: 1px solid rgba(0, 0, 0, 0.05);

  @media screen and (max-width: 1024px) {
    min-height: 100%;
    max-height: 100%;
    padding-bottom: 0;
  }
`}
`;

const Lock = styled.div<{ sidebarLocked: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.brandLightGrey};
  color: ${({ theme }) => theme.colors.brandBlue};
  cursor: pointer;

  @media screen and (max-width: 1024px) {
    display: none;
  }

  ${({ sidebarLocked }) =>
    sidebarLocked
      ? `
    svg {
      transform: rotate(-180deg);
    }
`
      : undefined};
`;

const TabsHeading = styled.h6`
  color: #12193c;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin: 0px 0px 16px 14px;
  padding: 0;
`;
